import React, { useRef, useEffect, useState } from "react";

/**
 * Hybrid flavor image: auto-detects aspect ratio and sets data-aspect for CSS
 * Also handles loading states and image placeholders
 */
export default function FlavorImage({ src, alt }) {
  const imgRef = useRef(null);
  const [aspect, setAspect] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  // Default image to use as fallback
  const DEFAULT_IMAGE = (import.meta.env.BASE_URL || '') + '/products/good-vibes-500.jpg';
  
  // Ensure src has BASE_URL if it's a relative path
  const processedSrc = src && !src.startsWith('http') && !src.startsWith(import.meta.env.BASE_URL || '')
    ? (import.meta.env.BASE_URL || '') + (src.startsWith('/') ? src : `/${src}`)
    : src;
  
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    
    const img = imgRef.current;
    if (!img) return;
    
    function handleLoad() {
      setIsLoading(false);
      if (img.naturalWidth && img.naturalHeight) {
        const ratio = img.naturalWidth / img.naturalHeight;
        if (ratio < 0.8) setAspect("tall");
        else if (ratio > 1.2) setAspect("wide");
        else setAspect("");
      }
    }
    
    function handleError() {
      setIsLoading(false);
      setHasError(true);
    }
    
    if (img.complete) handleLoad();
    else {
      img.addEventListener("load", handleLoad);
      img.addEventListener("error", handleError);
    }
    
    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [processedSrc]);

  return (
    <div className={`product-flavor-image ${isLoading ? 'loading' : ''} ${hasError ? 'error' : ''}`}>
      <img
        ref={imgRef}
        src={processedSrc || DEFAULT_IMAGE}
        alt={alt}
        loading="lazy"
        decoding="async"
        data-aspect={aspect || undefined}
        onError={(e) => {
          if (processedSrc !== DEFAULT_IMAGE) {
            e.target.src = DEFAULT_IMAGE;
          }
        }}
      />
      {isLoading && <div className="image-loader">Loading...</div>}
    </div>
  );
}
